import MessageReactions from './MessageReactions';
import React, { useMemo } from 'react';
import { StyleSheet, Platform, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import IconButton from '@/components/Buttons/IconButton';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import PropTypes from 'prop-types';
import { useModelStore, useAgentStore, useUIStore, useModalStore } from '@/store';
import { useRouter } from 'expo-router';
import { MESSAGE_LAYOUT, MESSAGE_REGENERATE_TYPES } from '@/constants/config';
import { BAD_INFERENCE_JOB_STATUSES } from '@/constants/utils/message';
import useResponsiveness from '@/hooks/useResponsiveness';
import useDeckAvailability from '@/hooks/useDeckAvailability';
import useTranslation from '@/hooks/useTranslation';
import Tooltip from '@/components/Tooltip';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import useActiveEditorNoun from '@/hooks/useActiveEditorNoun';
import { useCanShareChat } from '@/hooks/useCanShareChat';
import { canUseDesktopEditors } from '@/constants/utils/files';


// Constants
const DEFAULT_BUTTON_SIZE = 28;
const DEFAULT_ICON_SIZE = 13;
const AVATAR_SIZE = 24;

// Keyed by the noun the editor panel registers, so the label names the artifact
// the user is actually looking at.
const ADD_TO_EDITOR_LABEL_KEYS = {
  document: 'messages.addToEditor.addToDocument',
  spreadsheet: 'messages.addToEditor.addToSpreadsheet',
  presentation: 'messages.addToEditor.addToPresentation',
};

const OPEN_IN_EDITOR_ICON = {
  document: 'file-word',
  spreadsheet: 'file-excel',
};


// Avatar dropdown component for both models and agents
const AvatarDropdown = React.memo(({ item, type, colors, styles }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const { openModal } = useModalStore(state => state.actions);
  const router = useRouter();

  const handleOnSelectModel = () => {
    openModal('ModelDetailsModal', { modelId: item.id });
  };

  const handleOnStartNewChatWithModel = () => {
    router.push(`/m/${item.id}`);
  };

  const handleOnViewAllModels = () => {
    if (item?.company?.id) {
      router.push(`/models?company=${item.company.id}`);
    } else {
      router.push('/models');
    }
  };

  const handleOnSelectAgent = () => {
    openModal('AgentDetailsModal', { agentId: item.id });
  };

  const handleOnStartNewChatWithAgent = () => {
    router.push(`/g/${item.id}`);
  };

  const handleOnViewAllAgents = () => {
    router.push('/agents');
  };

  const renderContent = () => {
    if (type === 'model') {
      const attribution = item?.company?.attribution?.replace('{model}', item.name);
      return (
        <>
          {!!attribution && (
            <View style={{
              padding: 10,
              borderRadius: 8,
              backgroundColor: 'rgba(190, 190, 190, 0.2)',
              marginBottom: 8,
            }}>
              <Text size="s" color="secondary" style={{ fontStyle: 'italic', opacity: 1 }}>
                {attribution}
              </Text>
            </View>
          )}
          <DropdownMenuItem
            title={t('messages.actions.viewModelDetails')}
            icon="circle-info"
            onSelect={handleOnSelectModel}
          />
          {item.company && (
            <DropdownMenuItem
              title={t('messages.actions.viewAllModelsBy').replace('%NAME%', item?.company?.name)}
              icon="layer-group"
              onSelect={handleOnViewAllModels}
            />
          )}
          <DropdownMenuItem
            title={t('messages.actions.startNewChatModel')}
            icon="pen-to-square"
            onSelect={handleOnStartNewChatWithModel}
          />
        </>
      );
    } else {
      return (
        <>
          <DropdownMenuItem
            title={item.name}
            description={item.description}
            imageUrl={item.avatarPath}
            onSelect={handleOnSelectAgent}
          />
          <DropdownMenuItem
            title={t('messages.actions.viewAllAgents')}
            icon="train"
            onSelect={handleOnViewAllAgents}
          />
          <DropdownMenuItem
            title={t('messages.actions.startNewChat')}
            icon="pen-to-square"
            onSelect={handleOnStartNewChatWithAgent}
          />
        </>
      );
    }
  };

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <Tooltip label={type === 'model' ? t('messages.actions.model') : t('messages.actions.agent')}>
          <Image
            source={{ uri: type === 'model' ? item.logoPath : item.avatarPath }}
            style={[
              styles.avatar,
              isOpen && { borderColor: colors.primary },
            ]}
            accessibilityLabel={`${item.name} ${type === 'model' ? 'logo' : 'avatar'}`}
          />
        </Tooltip>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content 
        className="DropdownMenuContent"
        sideOffset={5}
        align="start"
      >
        {renderContent()}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

AvatarDropdown.propTypes = {
  item: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['model', 'agent']).isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

AvatarDropdown.displayName = 'AvatarDropdown';

// Create styles outside component
const createStyles = (colors, isVisible, alignment, isXS, messageLayout, isUser) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    opacity: isVisible ? 1 : 0,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 180ms cubic-bezier(0.2, 0, 0, 1)',
      pointerEvents: isVisible ? 'auto' : 'none',
    }),
    backgroundColor: 'transparent',
    justifyContent: (alignment === 'right' || (isUser && messageLayout !== MESSAGE_LAYOUT.SLIM)) ? 'flex-end' : 'flex-start',
    // justifyContent: 'flex-start',
    height: DEFAULT_BUTTON_SIZE,
    gap: isXS ? 5 : 0,
  },
  actionButton: {
    padding: 1,
    marginRight: 0,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 160ms ease, background-color 160ms ease',
      boxSizing: 'border-box',
    }),
  },
  opacity07: {
    opacity: 0.7,
  },
  actionButtonHovered: {
    opacity: 1,
    backgroundColor: colors.solid_color_secondary,
  },
  addToEditorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    borderRadius: 6,
    marginRight: 4,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.2s ease',
      cursor: 'pointer',
      boxSizing: 'border-box',
    }),
  },
  addToEditorButtonHovered: {
    opacity: 1,
    backgroundColor: colors.solid_color_secondary,
  },
  tabbedGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    // borderWidth: 1,
    height: 28,
    // borderColor: colors.border_color_secondary,
    borderRadius: 6,
    ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
    padding: 2,
    gap: 2,
    marginRight: 4,
  },
  tabButton: {
    padding: 0,
    ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
    marginRight: 0,
    borderRadius: 6,
    height: DEFAULT_BUTTON_SIZE - 2,
    width: DEFAULT_BUTTON_SIZE - 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabButtonSelected: {
    backgroundColor: 'rgba(35, 200, 45, 0.175)',
    color: 'rgb(35, 200, 45)',
  },
  tabButtonSelectedReject: {
    backgroundColor: 'rgba(255, 40, 40, 0.2)',
    color: 'rgb(255, 40, 40)',
  },
  attribution: {
    marginLeft: 4,
    opacity: 0.7,
  },
  avatarsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 0,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    marginRight: 4,
  },
  userAvatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 9999,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    marginRight: 4,
  },
  firstAvatar: {
    marginLeft: 0,
  },
});

const EMPTY_AGENT_IDS = [];

// Model and agent avatars component updated to use dropdowns
const ModelAgentAvatars = React.memo(({ modelIds, agentIds, colors, styles }) => {
  const getDeepModelById = useModelStore(state => state.getDeepModelById);
  const getAgentById = useAgentStore(state => state.getAgentById);
  const modelRecords = useModelStore(state => state.models);
  const agentRecords = useAgentStore(state => state.agents);

  // : todo :
  // if none, use conversation.agentId

  // Memoize model/agent lookups to prevent expensive recalculations on every render
  const models = React.useMemo(() => 
    modelIds?.flatMap(id => {
      const model = modelRecords[id] ? getDeepModelById(id) : null;
      return model ? [model] : [];
    }) || [],
    [modelIds, getDeepModelById, modelRecords]
  );
  const agents = React.useMemo(() => 
    agentIds?.flatMap(id => {
      const agent = agentRecords[id] ? getAgentById(id) : null;
      return agent ? [agent] : [];
    }) || [],
    [agentIds, getAgentById, agentRecords]
  );

  if (models.length === 0 && agents.length === 0) return null;

  return (
    <View style={styles.avatarsContainer}>
      {models.map((model, index) => (
        <View key={`model-${model.id}`} style={index === 0 && styles.firstAvatar}>
          <AvatarDropdown
            item={model}
            type="model"
            colors={colors}
            styles={styles}
          />
        </View>
      ))}
      {agents.map((agent, index) => (
        <View 
          key={`agent-${agent.id}`} 
          style={models.length === 0 && index === 0 && styles.firstAvatar}
        >
          <AvatarDropdown
            item={agent}
            type="agent"
            colors={colors}
            styles={styles}
          />
        </View>
      ))}
    </View>
  );
});

ModelAgentAvatars.propTypes = {
  modelIds: PropTypes.arrayOf(PropTypes.string),
  agentIds: PropTypes.arrayOf(PropTypes.string),
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

ModelAgentAvatars.displayName = 'ModelAgentAvatars';

// Memoized action button component
const ActionButton = React.memo(({ 
  icon, 
  onClick, 
  title, 
  iconSize, 
  buttonSize, 
  colors, 
  styles,
  isXS,
  iconIsSolid,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <IconButton
      icon={icon}
      iconSize={iconSize}
      color={colors.text_color_secondary}
      style={[
        styles.actionButton,
        !isHovered && styles.opacity07,
        isHovered && styles.actionButtonHovered
      ]}
      onPress={onClick}
      title={title}
      size={isXS ? 'small' : 'medium'}
      height={buttonSize}
      width={buttonSize}
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityHint={`Perform ${title.toLowerCase()} action`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      iconIsSolid={iconIsSolid}
      tooltip={title}
    />
  );
});

/**
 * Labeled sibling of ActionButton. This action only appears while an editor is
 * open, so unlike the always-present icons it names its target rather than
 * relying on a tooltip the user has to hunt for.
 */
const AddToEditorButton = React.memo(({
  onClick,
  label,
  iconSize,
  buttonSize,
  colors,
  styles,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <Pressable
      onPress={onClick}
      style={[
        styles.addToEditorButton,
        { height: buttonSize },
        !isHovered && styles.opacity07,
        isHovered && styles.addToEditorButtonHovered,
      ]}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Icon name="plus" size={iconSize} color={colors.text_color_secondary} />
      <Text size="xs" color="secondary" numberOfLines={1}>{label}</Text>
    </Pressable>
  );
});

AddToEditorButton.displayName = 'AddToEditorButton';

AddToEditorButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  iconSize: PropTypes.number.isRequired,
  buttonSize: PropTypes.number.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

ActionButton.propTypes = {
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  iconSize: PropTypes.number.isRequired,
  buttonSize: PropTypes.number.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  isXS: PropTypes.bool,
  iconIsSolid: PropTypes.bool,
};

ActionButton.displayName = 'ActionButton';

// Regenerate dropdown component
const RegenerateDropdown = React.memo(({ 
  onRegenerate,
  iconSize,
  isImage,
  isFile,
  buttonSize,
  colors,
  styles,
  isXS,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  const onTryAgain = React.useCallback(() => onRegenerate && onRegenerate(MESSAGE_REGENERATE_TYPES.AGAIN), [onRegenerate]);
  const onAddDetails = React.useCallback(() => onRegenerate && onRegenerate(MESSAGE_REGENERATE_TYPES.ADD_DETAILS), [onRegenerate]);
  const onMoreConcise = React.useCallback(() => onRegenerate && onRegenerate(MESSAGE_REGENERATE_TYPES.CONCISE), [onRegenerate]);

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
        <IconButton
          icon="arrow-rotate-right"
          iconSize={iconSize}
          color={colors.text_color_secondary}
          style={StyleSheet.flatten([
            styles.actionButton,
            !isHovered && styles.opacity07,
            isHovered && styles.actionButtonHovered
          ])}
          title={t('messages.actions.regenerate')}
          size={isXS ? 'small' : 'medium'}
          height={buttonSize}
          width={buttonSize}
          isHovering={isOpen}
          accessibilityRole="button"
          accessibilityLabel={t('messages.actions.regenerate')}
          accessibilityHint="Open regenerate options menu"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          tooltip={t('messages.actions.regenerate')}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent">
        <DropdownMenu.Item
          key="regenerate-try-again"
          onSelect={onTryAgain}
          className="DropdownMenuItem"
        >
          <DropdownMenu.ItemIcon
            className="DropdownMenuItemIcon"
            ios={{ name: 'arrow.clockwise', color: colors.text_color_primary }}
          >
            <Icon name="arrow-rotate-right" size={14} color={colors.text_color_primary} />
          </DropdownMenu.ItemIcon>
          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
            {t('messages.actions.tryAgain')}
          </DropdownMenu.ItemTitle>
        </DropdownMenu.Item>
        {
          !isImage && !isFile && (
            <>
              <DropdownMenu.Item
                key="regenerate-add-details"
                onSelect={onAddDetails}
                className="DropdownMenuItem"
              >
                <DropdownMenu.ItemIcon
                  className="DropdownMenuItemIcon"
                  ios={{ name: 'text.badge.plus', color: colors.text_color_primary }}
                >
                  <Icon name="arrow-up-wide-short" size={14} color={colors.text_color_primary} />
                </DropdownMenu.ItemIcon>
                <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                  {t('messages.actions.addDetails')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                key="regenerate-concise"
                onSelect={onMoreConcise}
                className="DropdownMenuItem"
              >
                <DropdownMenu.ItemIcon
                  className="DropdownMenuItemIcon"
                  ios={{ name: 'text.badge.minus', color: colors.text_color_primary }}
                >
                  <Icon name="arrows-to-dotted-line" size={14} color={colors.text_color_primary} />
                </DropdownMenu.ItemIcon>
                <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                  {t('messages.actions.moreConcise')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            </>
          )
        }
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

const ForkButton = React.memo(({ onFork, iconSize, buttonSize, colors, styles, isXS }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const supportsSideBySideFork = useDeckAvailability();

  const button = (
    <IconButton
      icon="code-fork"
      iconSize={iconSize}
      color={colors.text_color_secondary}
      style={StyleSheet.flatten([
        styles.actionButton,
        !isHovered && styles.opacity07,
        isHovered && styles.actionButtonHovered,
      ])}
      onPress={supportsSideBySideFork ? undefined : () => onFork('new-chat')}
      title={t('messages.actions.fork')}
      size={isXS ? 'small' : 'medium'}
      height={buttonSize}
      width={buttonSize}
      isHovering={isOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tooltip={isOpen ? null : t('messages.actions.fork')}
    />
  );

  if (!supportsSideBySideFork) return button;

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>{button}</DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent">
        <DropdownMenuItem
          onSelect={() => onFork('side-by-side')}
          icon="columns-3"
          title={t('messages.actions.forkSideBySide')}
        />
        <DropdownMenuItem
          onSelect={() => onFork('new-chat')}
          icon="message"
          title={t('messages.actions.forkNewChat')}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

// A non-interactive, text-selectable detail row used inside the Details
// submenu. Avoids Radix's DropdownMenu.Item so clicking/dragging to highlight
// text doesn't dismiss the dropdown.
const SelectableDetailRow = React.memo(({ icon, iconColor, title, value, colors }) => {
  const hasValue = value !== null && typeof value !== 'undefined' && value !== '';
  const selectableStyle = Platform.OS === 'web' ? {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    cursor: 'text',
  } : null;
  const wrapStyle = Platform.OS === 'web' ? {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    maxWidth: '100%',
  } : null;
  return (
    <View
      style={[{
        flexDirection: 'row',
        alignItems: hasValue ? 'flex-start' : 'center',
        paddingHorizontal: 5,
        paddingVertical: hasValue ? 8 : 0,
        minHeight: 30,
        borderRadius: 8,
      }, selectableStyle]}
    >
      {!!icon && (
        <View
          style={{
            height: 22,
            width: 22,
            marginRight: 4,
            alignItems: 'center',
            justifyContent: hasValue ? 'flex-start' : 'center',
            paddingTop: hasValue ? 4 : 0,
          }}
        >
          <Icon name={icon} size={14} color={iconColor || colors.text_color_tertiary} />
        </View>
      )}
      <View style={[{ flex: 1, minWidth: 0 }, selectableStyle]}>
        <Text size='n' color='primary' style={[selectableStyle, wrapStyle]}>
          {title}
        </Text>
        {hasValue && (
          <Text
            size='xs'
            color='tertiary'
            style={[{ marginTop: 4 }, selectableStyle, wrapStyle]}
          >
            {value}
          </Text>
        )}
      </View>
    </View>
  );
});

SelectableDetailRow.displayName = 'SelectableDetailRow';

SelectableDetailRow.propTypes = {
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  title: PropTypes.node.isRequired,
  value: PropTypes.node,
  colors: PropTypes.object.isRequired,
};

// New More dropdown with Report and Delete
const MoreDropdown = React.memo(({ 
  showDetails = true,
  menuContainer,
  onSpeak,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onExport,
  onReport,
  onEdit,
  onDelete,
  onAddToDocument,
  onAddToSpreadsheet,
  activeEditorNoun,
  iconSize,
  buttonSize,
  colors,
  styles,
  isXS,
  modelIds,
  agentIds,
  isImage,
  isFile,
  inferences,
  isUser,
  parameters,
  createdAt,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const supportsSideBySideFork = useDeckAvailability();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isDetailsSubOpen, setIsDetailsSubOpen] = React.useState(false);
  const [isForkSubOpen, setIsForkSubOpen] = React.useState(false);

  const getDeepModelById = useModelStore(state => state.getDeepModelById);
  const getAgentById = useAgentStore(state => state.getAgentById);
  
  // Memoize model/agent lookups to prevent expensive recalculations on every render
  const models = React.useMemo(() => 
    (modelIds || []).map(id => getDeepModelById(id)).filter(Boolean),
    [modelIds, getDeepModelById]
  );
  const agents = React.useMemo(() => 
    (agentIds || []).map(id => getAgentById(id)).filter(Boolean),
    [agentIds, getAgentById]
  );

  // Lazy-compute expensive values only when dropdown is open
  // This prevents lag when actions first appear after streaming ends
  const parameterDefMap = React.useMemo(() => {
    if (!isOpen) return new Map(); // Skip computation when closed
    const map = new Map();
    if (!Array.isArray(models)) return map;
    models.forEach(model => {
      const params = model?.parameters || {};
      Object.values(params).forEach(bucket => {
        if (Array.isArray(bucket)) {
          bucket.forEach(def => {
            if (def && typeof def === 'object') {
              if (def.id) {
                map.set(def.id, def);
              }
              if (def.name && !map.has(def.name)) {
                map.set(def.name, def);
              }
            }
          });
        }
      });
    });
    return map;
  }, [models, isOpen]);

  const latestInference = React.useMemo(() => {
    if (!isOpen) return null; // Skip computation when closed
    if (!Array.isArray(inferences) || inferences.length === 0) return null;
    const copy = [...inferences];
    copy.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
    return copy[0];
  }, [inferences, isOpen]);

  const details = React.useMemo(() => {
    if (!isOpen || !latestInference) return null; // Skip computation when closed
    const createdAt = latestInference?.createdAt ? new Date(latestInference.createdAt) : null;
    const createdAtStr = createdAt ? createdAt.toLocaleString() : '—';
    const durationMs = latestInference?.metrics?.totalDurationMS ?? null;
    const durationStr = typeof durationMs === 'number' ? (durationMs >= 1000 ? `${(durationMs / 1000).toFixed(2)}s` : `${durationMs}ms`) : '—';
    const totalCost = latestInference?.creditCost?.total;
    const pendingUsage = ['pending', 'review'].includes(latestInference?.creditCost?.reconciliation?.status);
    const totalCostStr = pendingUsage ? 'Pending usage confirmation' : (typeof totalCost === 'number') ? `${totalCost}` : '—';
    const isRefunded = !!latestInference?.creditCost?.isRefunded;
    const inferenceTaskId = latestInference?.initiators?.inferenceTaskId;

    return { createdAtStr, durationStr, totalCostStr, isRefunded, inferenceTaskId };
  }, [latestInference, isOpen]);

  const userParameterEntries = React.useMemo(() => {
    if (!isOpen) return null; // Skip computation when closed
    if (!parameters || typeof parameters !== 'object') return null;
    const entries = [];
    Object.values(parameters).forEach(inner => {
      if (!inner || typeof inner !== 'object') return;
      Object.entries(inner).forEach(([k, v]) => {
        entries.push([k, v]);
      });
    });
    return entries.length > 0 ? entries : null;
  }, [parameters, isOpen]);

  // Prompt-run messages stash the originating recipe slug flat on
  // `message.parameters` (see backend prompt_runner). Surface a quick link
  // back to that prompt's detail page.
  const promptSlug = React.useMemo(() => {
    if (!parameters || typeof parameters !== 'object') return null;
    const slug = parameters.___promptSlug || parameters.promptSlug;
    return typeof slug === 'string' && slug.trim() ? slug.trim() : null;
  }, [parameters]);

  const handleViewPrompt = React.useCallback((event) => {
    if (Platform.OS === 'web' && event?.preventDefault) event.preventDefault();
    if (promptSlug) router.push(`/prompts/${encodeURIComponent(promptSlug)}`);
  }, [promptSlug, router]);

  const humanizeParamKey = React.useCallback((key) => {
    if (!key && key !== 0) return '';
    const str = String(key)
      .replace(/[_\-.]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
    if (!str) return '';
    return str
      .split(' ')
      .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
      .join(' ');
  }, []);

  const formatPrimitive = React.useCallback((value) => {
    if (value === null || typeof value === 'undefined') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value.trim() || '—';
    return null;
  }, []);

  const formatParamValue = React.useCallback((value) => {
    const prim = formatPrimitive(value);
    if (prim !== null) return prim;
    if (Array.isArray(value)) {
      // Render simple primitive arrays as comma-separated lists
      if (value.every(v => formatPrimitive(v) !== null)) {
        return value.map(v => formatPrimitive(v)).join(', ');
      }
      try { return JSON.stringify(value, null, 2); } catch (e) { return String(value); }
    }
    if (value && typeof value === 'object') {
      const keys = Object.keys(value);
      // If the object wraps a single primitive field (e.g. { description: "..." }
      // or { prompt: "..." }), unwrap it for a cleaner display.
      if (keys.length === 1) {
        const innerPrim = formatPrimitive(value[keys[0]]);
        if (innerPrim !== null) return innerPrim;
      }
      // Otherwise render each inner field on its own line with the
      // humanized key bolded for readability.
      return (
        <>
          {keys.map((k, idx) => {
            const inner = value[k];
            const innerPrim = formatPrimitive(inner);
            let valueStr;
            if (innerPrim !== null) {
              valueStr = innerPrim;
            } else {
              try { valueStr = JSON.stringify(inner); } catch (e) { valueStr = String(inner); }
            }
            return (
              <Text key={k} color='tertiary' size='xs'>
                {idx > 0 ? '\n' : ''}
                <Text color='secondary' size='xs' weight='bold'>{humanizeParamKey(k)}</Text>
                {`: ${valueStr}`}
              </Text>
            );
          })}
        </>
      );
    }
    return String(value);
  }, [formatPrimitive, humanizeParamKey]);

  const userCreatedAtStr = React.useMemo(() => {
    if (!isOpen) return null; // Skip computation when closed
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleString();
  }, [createdAt, isOpen]);

  const messageCreatedAtStr = React.useMemo(() => {
    if (!isOpen || !createdAt) return null;
    const date = new Date(createdAt);
    if (isNaN(date.getTime())) return null;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfMessageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDifference = Math.round((startOfToday - startOfMessageDay) / 86400000);
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (dayDifference === 0) return `Today, ${time}`;
    if (dayDifference === 1) return `Yesterday, ${time}`;
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }, [createdAt, isOpen]);

  const handleDownload = React.useCallback(() => {
    onExport?.(isImage ? 'image' : isFile ? 'file' : 'plain');
  }, [onExport, isImage, isFile]);

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
        <IconButton
          icon="ellipsis"
          iconSize={iconSize}
          color={colors.text_color_secondary}
          style={StyleSheet.flatten([
            styles.actionButton,
            !isHovered && styles.opacity07,
            isHovered && styles.actionButtonHovered
          ])}
          title={t('messages.actions.more')}
          size={isXS ? 'small' : 'medium'}
          height={buttonSize}
          width={buttonSize}
          isHovering={isOpen}
          accessibilityRole="button"
          accessibilityLabel={t('messages.actions.moreActions')}
          accessibilityHint="Open more actions menu"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          tooltip={isOpen ? null : t('messages.actions.moreActions')}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content portalContainer={menuContainer} className="DropdownMenuContent" style={{ minWidth: 240 }}>
        {!!messageCreatedAtStr && (
          <DropdownMenu.Label className="DropdownMenuLabel">
            <Text color="tertiary" size="s">{messageCreatedAtStr}</Text>
          </DropdownMenu.Label>
        )}
        {!!onSpeak && !isUser && !isImage && !isFile && (
          <DropdownMenu.Item
            key="speak"
            onSelect={onSpeak}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'speaker.wave.2', color: colors.text_color_primary }}
            >
              <Icon name="volume-high" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.speak')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onStartNewTask && (
          <DropdownMenuItem key="start-new-work-task" onSelect={onStartNewTask}
            title="Start a new task from this message" icon="arrow-up-right-from-square" />
        )}
        {!!onFork && supportsSideBySideFork && (
          <DropdownMenu.Sub open={isForkSubOpen} onOpenChange={setIsForkSubOpen}>
            <DropdownMenu.SubTrigger className="DropdownMenuItem" key="fork-trigger">
              <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                <Icon name="code-fork" size={14} color={colors.text_color_primary} />
              </DropdownMenu.ItemIcon>
              <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                {t('messages.actions.fork')}
              </DropdownMenu.ItemTitle>
              <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
              </DropdownMenu.ItemIcon>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent
              className="DropdownMenuSubContent"
              side={isXS ? 'bottom' : 'right'}
              align={isXS ? 'center' : 'start'}
              sideOffset={isXS ? 6 : 4}
            >
              <DropdownMenuItem
                onSelect={() => onFork('side-by-side')}
                icon="columns-3"
                title={t('messages.actions.forkSideBySide')}
              />
              <DropdownMenuItem
                onSelect={() => onFork('new-chat')}
                icon="message"
                title={t('messages.actions.forkNewChat')}
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        )}
        {/* Written out as a raw zeego item instead of <DropdownMenuItem />: the
            native iOS menu only picks up children whose element type is
            DropdownMenu.Item, so wrapper components are dropped from the menu. */}
        {!!onFork && !supportsSideBySideFork && (
          <DropdownMenu.Item
            key="fork-new-chat"
            onSelect={() => onFork('new-chat')}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'arrow.triangle.branch', color: colors.text_color_primary }}
            >
              <Icon name="code-fork" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.fork')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onBookmark && (
          <DropdownMenu.Item
            key="bookmark"
            onSelect={onBookmark}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'bookmark', color: colors.text_color_primary }}
            >
              <Icon name="bookmark" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.bookmark')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {/* Desktop web only: open (or create) a document/spreadsheet from this
            reply when that editor isn't already open beside the chat. */}
        {!!onAddToDocument && !isUser && activeEditorNoun !== 'document' && (
          <DropdownMenu.Item
            key="add-to-document"
            onSelect={onAddToDocument}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'doc.text', color: colors.text_color_primary }}
            >
              <Icon name={OPEN_IN_EDITOR_ICON.document} size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.addToEditor.openInDocument')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onAddToSpreadsheet && !isUser && activeEditorNoun !== 'spreadsheet' && (
          <DropdownMenu.Item
            key="add-to-spreadsheet"
            onSelect={onAddToSpreadsheet}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'tablecells', color: colors.text_color_primary }}
            >
              <Icon name={OPEN_IN_EDITOR_ICON.spreadsheet} size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.addToEditor.openInSpreadsheet')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onExport && (
          <DropdownMenu.Item
            key="download"
            onSelect={handleDownload}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'square.and.arrow.down', color: colors.text_color_primary }}
            >
              <Icon name="arrow-down-to-line" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.download')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onFeedback && !isUser && (
          <DropdownMenu.Item
            key="feedback"
            onSelect={onFeedback}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'hand.thumbsdown', color: colors.text_color_primary }}
            >
              <Icon name="thumbs-down" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.feedback')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {(onSpeak || onFork || onBookmark || onAddToDocument || onAddToSpreadsheet || onExport || onFeedback) && (promptSlug || (onEdit && isUser) || onReport || onDelete || (Platform.OS === 'web' && !isUser && showDetails)) && (
          <DropdownMenu.Separator className="DropdownMenuSeparator" />
        )}
        {!!promptSlug && (
          <DropdownMenu.Item
            key="view-prompt"
            onSelect={handleViewPrompt}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'wand.and.stars', color: colors.text_color_primary }}
            >
              <Icon name="wand-magic-sparkles" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.viewPrompt') || 'View prompt'}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onEdit && isUser && (
          <DropdownMenu.Item
            key="edit"
            onSelect={onEdit}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'pencil', color: colors.text_color_primary }}
            >
              <Icon name="pen" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.edit')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onReport && (
          <DropdownMenu.Item
            key="report"
            onSelect={onReport}
            className="DropdownMenuItem"
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'flag', color: colors.text_color_primary }}
            >
              <Icon name="flag" size={14} color={colors.text_color_primary} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.report')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}
        {!!onDelete && (
          <DropdownMenu.Item
            key="delete"
            onSelect={onDelete}
            className="DropdownMenuItem DropdownMenuItemDanger"
            destructive
          >
            <DropdownMenu.ItemIcon
              className="DropdownMenuItemIcon"
              ios={{ name: 'trash', color: colors.color_destructive }}
            >
              <Icon name="trash" size={14} color={colors.color_destructive} />
            </DropdownMenu.ItemIcon>
            <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
              {t('messages.actions.delete')}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        )}

        {/* Details submenu - web only, not shown on native iOS */}
        {Platform.OS === 'web' && !isUser && showDetails && (
          <>
            {/* Details submenu */}
            <DropdownMenu.Sub
              open={isDetailsSubOpen}
              onOpenChange={setIsDetailsSubOpen}
            >
              <DropdownMenu.SubTrigger className="DropdownMenuItem" key="details-trigger">
                <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                  <Icon name="circle-info" size={14} color={colors.text_color_primary} />
                </DropdownMenu.ItemIcon>
                <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                  {t('messages.actions.details')}
                </DropdownMenu.ItemTitle>
                <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                  <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                </DropdownMenu.ItemIcon>
              </DropdownMenu.SubTrigger>

              <DropdownMenu.SubContent
                className="DropdownMenuSubContent"
                side={isXS ? 'bottom' : 'right'}
                align={isXS ? 'center' : 'start'}
                sideOffset={isXS ? 6 : 4}
              >
                {isXS && (
                  <>
                    <DropdownMenu.Item
                      key="details-back"
                      className="DropdownMenuItem"
                      onSelect={(event) => {
                        if (Platform.OS === 'web' && event?.preventDefault) {
                          event.preventDefault();
                        }
                        setIsDetailsSubOpen(false);
                      }}
                    >
                      <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                        <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                      </DropdownMenu.ItemIcon>
                      <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                        {t('messages.actions.back')}
                      </DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="DropdownMenuSeparator" />
                  </>
                )}
                <>
                  {details && (
                    <>
                      <SelectableDetailRow
                        key="details-created"
                        icon="calendar-days"
                        title={`${details.createdAtStr}`}
                        colors={colors}
                      />
                      <SelectableDetailRow
                        key="details-duration"
                        icon="stopwatch"
                        title={t('messages.actions.duration').replace('%VALUE%', details.durationStr)}
                        colors={colors}
                      />
                      <SelectableDetailRow
                        key="details-cost"
                        icon="coins"
                        title={t('messages.actions.totalCreditCost').replace('%VALUE%', details.totalCostStr)}
                        colors={colors}
                      />
                      {details.inferenceTaskId && (
                         <DropdownMenuItem
                           key="details-task-id"
                           icon="robot"
                           title={t('messages.actions.runByTask')}
                           onSelect={() => {
                             openModal('CreateTaskModal', { taskId: details.inferenceTaskId });
                           }}
                         />
                      )}
                      {details.isRefunded && (
                        <SelectableDetailRow
                          key="details-refunded"
                          icon="arrow-rotate-left"
                          title={t('messages.actions.refunded')}
                          colors={colors}
                        />
                      )}
                    </>
                  )}
                  {Array.isArray(userParameterEntries) && userParameterEntries.length > 0 && (
                    <>
                      <DropdownMenuLabel text={t('messages.actions.parameters')} />
                      {userParameterEntries.map(([key, value]) => {
                        const def = parameterDefMap.get(key);
                        const paramName = def?.ui?.displayName || def?.name || key;
                        const paramValue = formatParamValue(value);
                        const itemIcon = def?.ui?.icon || def?.ui?.displayIcon || 'tag';
                        return (
                          <SelectableDetailRow
                            key={`assistant-param-${key}`}
                            icon={itemIcon}
                            title={paramName}
                            value={paramValue}
                            colors={colors}
                          />
                        );
                      })}
                    </>
                  )}
                  {!details && !(Array.isArray(userParameterEntries) && userParameterEntries.length > 0) && (
                    <SelectableDetailRow
                      key="details-none"
                      icon="circle-info"
                      title={t('messages.actions.noDetailsAvailable')}
                      colors={colors}
                    />
                  )}
                </>
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          </>
        )}

        {Platform.OS === 'web' && isUser && (
          <>
            <DropdownMenu.Sub
              open={isDetailsSubOpen}
              onOpenChange={setIsDetailsSubOpen}
            >
              <DropdownMenu.SubTrigger className="DropdownMenuItem" key="user-details-trigger">
                <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                  <Icon name="circle-info" size={14} color={colors.text_color_primary} />
                </DropdownMenu.ItemIcon>
                <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                  {t('messages.actions.details')}
                </DropdownMenu.ItemTitle>
                <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                  <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                </DropdownMenu.ItemIcon>
              </DropdownMenu.SubTrigger>

              <DropdownMenu.SubContent
                className="DropdownMenuSubContent"
                side={isXS ? 'bottom' : 'right'}
                align={isXS ? 'center' : 'start'}
                sideOffset={isXS ? 6 : 4}
              >
                {isXS && (
                  <>
                    <DropdownMenu.Item
                      key="user-details-back"
                      className="DropdownMenuItem"
                      onSelect={(event) => {
                        if (Platform.OS === 'web' && event?.preventDefault) {
                          event.preventDefault();
                        }
                        setIsDetailsSubOpen(false);
                      }}
                    >
                      <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                        <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                      </DropdownMenu.ItemIcon>
                      <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                        {t('messages.actions.back')}
                      </DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="DropdownMenuSeparator" />
                  </>
                )}
                {(userCreatedAtStr || userParameterEntries) ? (
                  <>
                    {userCreatedAtStr && (
                      <SelectableDetailRow
                        key="user-details-created"
                        icon="calendar-days"
                        title={`${userCreatedAtStr}`}
                        colors={colors}
                      />
                    )}
                    {Array.isArray(userParameterEntries) && userParameterEntries.length > 0 && (
                      <DropdownMenuLabel text={t('messages.actions.parameters')} />
                    )}
                    {Array.isArray(userParameterEntries) && userParameterEntries.map(([key, value]) => {
                      const def = parameterDefMap.get(key);
                      const paramName = def?.ui?.displayName || def?.name || key;
                      const paramValue = formatParamValue(value);
                      const itemIcon = def?.ui?.icon || def?.ui?.displayIcon || 'tag';
                      return (
                        <SelectableDetailRow
                          key={`user-detail-${key}`}
                          icon={itemIcon}
                          title={paramName}
                          value={paramValue}
                          colors={colors}
                        />
                      );
                    })}
                    {Array.isArray(models) && models.length > 0 && (
                      <>
                        <DropdownMenuLabel text={t('messages.actions.models')} />
                        {models.map((model) => (
                          <DropdownMenuItem
                            key={`user-model-${model.id}`}
                            imageUrl={model.logoPath}
                            title={model.name}
                            isDisabled
                          />
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <SelectableDetailRow
                    key="user-details-none"
                    icon="circle-info"
                    title={t('messages.actions.noDetailsAvailable')}
                    colors={colors}
                  />
                )}
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          </>
        )}

      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

function MessageActions({ 
  showDetails = true,
  menuContainer,
  conversationId,
  message,
  isUser,
  userName,
  userAvatar,
  isVisible,
  buttonSize = DEFAULT_BUTTON_SIZE,
  alignment = 'left',
  showAttribution = false,
  iconSize = DEFAULT_ICON_SIZE,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onReply,
  onEdit,
  onDelete,
  onExport,
  isLastMessage,
  modelIds,
  agentIds,
  isAutoMode = false,
  isImage,
  isFile,
  onReport,
  inferences,
  parameters,
  createdAt,
  showPreferenceIcons,
  isGroupedResponsePreferred,
  onPreferResponse,
  onRejectResponse,
  workActions,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const messageLayout = useUIStore(state => state.messageLayout);
  const showMessageActions = useUIStore(state => state.showMessageActions);
  const isWorkChrome = workActions === 'chrome';
  const canShare = useCanShareChat(conversationId);
  const isWorkThread = workActions === 'chrome' || workActions === 'final';
  const hideWorkRetry = isWorkThread;
  // Null unless a document/spreadsheet/presentation panel is mounted beside the
  // chat, which is what keeps this action hidden the rest of the time.
  const activeEditorNoun = useActiveEditorNoun();
  // Three-dot "open in editor" entries are desktop-web only (same gate as the
  // editor panels themselves). Re-check on each render so resizing past the
  // breakpoint hides them without a reload.
  const desktopEditorsAvailable = Platform.OS === 'web' && !isXS && canUseDesktopEditors();

  // Memoize expensive inference check to avoid re-computation on every render
  const hasAnyInferencesWithIncompleteJobs = useMemo(() => 
    !isUser && Array.isArray(inferences) && inferences.length > 0 && 
    inferences.some((inf) => Array.isArray(inf?.output?.jobs) && 
      inf.output.jobs.some((job) => BAD_INFERENCE_JOB_STATUSES.has(job?.status))),
    [isUser, inferences]
  );
  const showChatResponseActions = !isUser && !isWorkThread && !hasAnyInferencesWithIncompleteJobs;
  const showSpeakButton = showChatResponseActions && !!onSpeak && !isImage && !isFile;
  // Work final replies keep Branch chat in the toolbar (next to Share) instead
  // of the overflow menu. Progress/chrome rows have neither Share nor More.
  const showForkButton = !isUser && !isWorkChrome && !!onFork && !hasAnyInferencesWithIncompleteJobs;

  // If showMessageActions is false, we should only show actions on hover
  const shouldShow = showMessageActions || isVisible;

  // Memoize styles
  const styles = useMemo(() => 
    createStyles(colors, shouldShow, alignment, isXS, messageLayout, isUser), 
    [colors, shouldShow, alignment, isXS, messageLayout, isUser]
  );

  const content = (
    <View 
      style={styles.container}
      accessibilityRole={isWorkChrome ? undefined : 'toolbar'}
      accessibilityLabel={isWorkChrome ? undefined : 'Message actions'}
    >
      {
        !isUser && !isWorkChrome && (
          <ModelAgentAvatars
            // Auto mode picks a model server-side; don't surface which one.
            modelIds={isAutoMode ? [] : modelIds}
            // The bot avatar already sits beside the response body, so the
            // footer only repeats model attribution.
            agentIds={EMPTY_AGENT_IDS}
            colors={colors}
            styles={styles}
          />
        )
      }
      {isUser && !!userAvatar && (
        <Tooltip label={userName}>
          <Image
            source={{ uri: userAvatar }}
            style={styles.userAvatar}
            accessibilityLabel={`${userName || 'User'} avatar`}
          />
        </Tooltip>
      )}
      {onCopy && !isWorkChrome && !hasAnyInferencesWithIncompleteJobs && (
        <ActionButton
          icon="copy"
          onClick={onCopy}
          title={t('messages.actions.copy')}
          iconSize={iconSize}
          buttonSize={buttonSize}
          colors={colors}
          styles={styles}
          isXS={isXS}
        />
      )}
      {!isUser && message && !isWorkChrome && !hasAnyInferencesWithIncompleteJobs && (
        <MessageReactions
          conversationId={conversationId}
          message={message}
          isOwner={!!onShare}
          buttonSize={buttonSize}
          iconSize={iconSize}
          buttonStyle={[styles.actionButton, styles.opacity07]}
        />
      )}
      {/* Work already delivers files as artifacts; don't offer "add this
          reply to the open presentation/doc" on chrome or the final reply. */}
      {onAddToEditor && !isUser && !isWorkThread && activeEditorNoun && !hasAnyInferencesWithIncompleteJobs && (
        <AddToEditorButton
          onClick={onAddToEditor}
          label={t(ADD_TO_EDITOR_LABEL_KEYS[activeEditorNoun] || 'messages.addToEditor.addToDocument')}
          iconSize={iconSize}
          buttonSize={buttonSize}
          colors={colors}
          styles={styles}
        />
      )}
      {onShare && canShare && !isUser && !isWorkChrome && !hasAnyInferencesWithIncompleteJobs && (
        <ActionButton
          icon="arrow-up-from-bracket"
          onClick={onShare}
          title={t('messages.actions.share')}
          iconSize={iconSize}
          buttonSize={buttonSize}
          colors={colors}
          styles={styles}
          isXS={isXS}
        />
      )}
      {onRegenerate && isLastMessage && !isUser && !hideWorkRetry && (
        <RegenerateDropdown
          onRegenerate={onRegenerate}
          iconSize={iconSize}
          buttonSize={buttonSize}
          isImage={isImage}
          isFile={isFile}
          colors={colors}
          styles={styles}
          isXS={isXS}
        />
      )}
      {showForkButton && (
        <ForkButton
          onFork={onFork}
          iconSize={iconSize}
          buttonSize={buttonSize}
          colors={colors}
          styles={styles}
          isXS={isXS}
        />
      )}
      {showSpeakButton && (
        <ActionButton
          icon="volume-high"
          onClick={onSpeak}
          title={t('messages.actions.speak')}
          iconSize={iconSize}
          buttonSize={buttonSize}
          colors={colors}
          styles={styles}
          isXS={isXS}
        />
      )}
      {showPreferenceIcons && !isWorkChrome && (onPreferResponse || onRejectResponse) && (
        <View style={styles.tabbedGroup}>
          {onPreferResponse && (
            <Tooltip label={t('messages.actions.preferResponse')}>
              <IconButton
                icon="check"
                onPress={onPreferResponse}
                iconSize={iconSize}
                iconColor={isGroupedResponsePreferred === 1 ? 'rgb(35, 200, 45)' : colors.text_color_secondary}
                style={[
                  styles.tabButton,
                  isGroupedResponsePreferred === 1 && styles.tabButtonSelected
                ]}
                size="small"
                accessibilityRole="button"
                accessibilityLabel="Prefer response"
              />
            </Tooltip>
          )}
          {onRejectResponse && (
            <Tooltip label={t('messages.actions.rejectResponse')}>
              <IconButton
                icon="xmark"
                onPress={onRejectResponse}
                iconSize={iconSize}
                iconColor={isGroupedResponsePreferred === 0 ? 'rgb(255, 40, 40)' : colors.text_color_secondary}
                style={[
                  styles.tabButton,
                  isGroupedResponsePreferred === 0 && styles.tabButtonSelectedReject
                ]}
                size="small"
                accessibilityRole="button"
                accessibilityLabel="Reject response"
              />
            </Tooltip>
          )}
        </View>
      )}
      {!isWorkChrome && (
        <MoreDropdown
          showDetails={showDetails}
          menuContainer={menuContainer}
          onStartNewTask={onStartNewTask}
          onSpeak={!hasAnyInferencesWithIncompleteJobs && !showSpeakButton ? onSpeak : null}
          onFork={!hasAnyInferencesWithIncompleteJobs && !showForkButton ? onFork : null}
          onBookmark={!hasAnyInferencesWithIncompleteJobs ? onBookmark : null}
          onFeedback={onFeedback}
          onExport={!hasAnyInferencesWithIncompleteJobs ? onExport : null}
          onReport={onReport}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddToDocument={desktopEditorsAvailable && !hasAnyInferencesWithIncompleteJobs ? onAddToDocument : null}
          onAddToSpreadsheet={desktopEditorsAvailable && !hasAnyInferencesWithIncompleteJobs ? onAddToSpreadsheet : null}
          activeEditorNoun={activeEditorNoun}
          iconSize={iconSize}
          buttonSize={buttonSize}
          colors={colors}
          styles={styles}
          isXS={isXS}
          modelIds={modelIds}
          agentIds={agentIds}
          isImage={Array.isArray(isImage ? [1] : []) && isImage}
          isFile={Array.isArray(isFile ? [1] : []) && isFile}
          inferences={inferences}
          isUser={isUser}
          parameters={parameters}
          createdAt={createdAt}
        />
      )}
      {showAttribution && !isUser && (
        <Text 
          size='s'
          style={styles.attribution}
          color='primary'
          accessibilityRole="text"
        >
          GAB.AI
        </Text>
      )}
    </View>
  );

  return content;
}

MessageActions.propTypes = {
  showDetails: PropTypes.bool,
  menuContainer: PropTypes.object,
  conversationId: PropTypes.string,
  message: PropTypes.object,
  isUser: PropTypes.bool,
  userName: PropTypes.string,
  userAvatar: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  buttonSize: PropTypes.number,
  alignment: PropTypes.oneOf(['left', 'right']),
  showAttribution: PropTypes.bool,
  iconSize: PropTypes.number,
  onCopy: PropTypes.func,
  onAddToEditor: PropTypes.func,
  onAddToDocument: PropTypes.func,
  onAddToSpreadsheet: PropTypes.func,
  onShare: PropTypes.func,
  onSpeak: PropTypes.func,
  onRegenerate: PropTypes.func,
  onFork: PropTypes.func,
  onBookmark: PropTypes.func,
  onFeedback: PropTypes.func,
  onReply: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onExport: PropTypes.func,
  isLastMessage: PropTypes.bool,
  modelIds: PropTypes.arrayOf(PropTypes.string),
  agentIds: PropTypes.arrayOf(PropTypes.string),
  isAutoMode: PropTypes.bool,
  isImage: PropTypes.bool,
  isFile: PropTypes.bool,
  onReport: PropTypes.func,
  inferences: PropTypes.arrayOf(PropTypes.object),
  parameters: PropTypes.object,
  createdAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  showPreferenceIcons: PropTypes.bool,
  isGroupedResponsePreferred: PropTypes.number,
  onPreferResponse: PropTypes.func,
  onRejectResponse: PropTypes.func,
  workActions: PropTypes.oneOf(['chrome', 'final']),
};

MessageActions.displayName = 'MessageActions';

export default React.memo(MessageActions);
